import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import portalAdminService from '../../services/portalAdminService';
import { profileService } from '../../services';
import PortalShell from './PortalShell';

/**
 * /portal/admin/applications/:id — full Portal on the left,
 * application content + Accept / Decline / Not Yet actions on the right.
 */
export default function PortalAdminApplicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [resume, setResume] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    portalAdminService.getApplication(id).then(async (r) => {
      if (r.success) {
        setApp(r.data);
        // Pull full Portal — public endpoint, no extra auth needed.
        if (r.data.userId) {
          const p = await profileService.getProfileByUserId(r.data.userId);
          if (p.success) setResume(p.data);
        }
      } else {
        setError(r.error || 'Could not load');
      }
    });
  }, [id]);

  const review = async (action) => {
    setBusy(true);
    setError(null);
    const r = await portalAdminService.reviewApplication(id, { action, decisionNote: note || null });
    setBusy(false);
    if (r.success) {
      navigate('/noir/admin/applications', { replace: true });
    } else {
      setError(r.error || 'Could not save');
    }
  };

  const extended = parseJSON(resume?.extendedProfile) || {};
  const gathering = parseJSON(resume?.gatheringTypes) || [];

  // Clicking the applicant's photo opens their full public Portal.
  const profileHref = resume?.handle
    ? `/${resume.handle}`
    : (resume?.userId || app?.userId) ? `/users/${resume?.userId || app?.userId}` : null;
  const openFullResume = () => { if (profileHref) navigate(profileHref); };

  return (
    <PortalShell viewerState="accepted">
      <div style={{ maxWidth: 1100, margin: '24px auto 80px', padding: '0 24px' }}>
        <button onClick={() => navigate('/noir/admin/applications')} className="portal-mono" style={{ background: 'none', border: 'none', color: 'rgba(236,231,218,0.34)', cursor: 'pointer', fontSize: 11, marginBottom: 24 }}>← ALL APPLICATIONS</button>

        <p className="portal-label" style={{ marginBottom: 14 }}>APPLICATION OF</p>
        <h1 className="portal-hero" style={{ marginBottom: 28 }}>{app?.displayName || '…'}</h1>

        {error && <p className="portal-mono" style={{ color: '#B4736A', marginBottom: 16 }}>{error}</p>}

        <div className="portal-grid-sidebar">
          {/* Left: Portal */}
          <div>
            <p className="portal-label" style={{ marginBottom: 12 }}>FULL PORTAL PAGE</p>
            <div className="portal-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
                <div
                  className="portal-avatar"
                  style={{ width: 96, height: 96, cursor: profileHref ? 'pointer' : 'default' }}
                  onClick={openFullResume}
                  onKeyDown={(e) => { if (profileHref && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openFullResume(); } }}
                  role={profileHref ? 'button' : undefined}
                  tabIndex={profileHref ? 0 : undefined}
                  title={profileHref ? 'Open full Portal' : undefined}
                >
                  {resume?.avatarUrl || resume?.faceUrl ? <img src={resume.avatarUrl || resume.faceUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                </div>
                <div>
                  <div className="portal-serif" style={{ fontSize: 22 }}>{resume?.displayName || app?.displayName}</div>
                  <div className="portal-mono" style={{ fontSize: 10, color: 'rgba(236,231,218,0.34)' }}>
                    {resume?.handle ? `@${resume.handle}` : ''}{resume?.location ? ` · ${resume.location.toUpperCase()}` : ''}
                  </div>
                </div>
              </div>

              {resume?.vibeCheck && (
                <ResumeSection label="VIBE CHECK">
                  <p className="portal-serif" style={{ fontStyle: 'italic', fontSize: 16, color: 'rgba(236,231,218,0.56)' }}>"{resume.vibeCheck}"</p>
                </ResumeSection>
              )}

              {Array.isArray(extended.musicArtists) && extended.musicArtists.some(Boolean) && (
                <ResumeSection label="MUSIC">
                  <ul style={{ margin: 0, paddingLeft: 20 }} className="portal-serif">
                    {extended.musicArtists.filter(Boolean).map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </ResumeSection>
              )}

              {Array.isArray(extended.strengths) && extended.strengths.length > 0 && (
                <ResumeSection label="STRENGTHS">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {extended.strengths.map((s, i) => <span key={i} className="portal-chip">{s}</span>)}
                  </div>
                </ResumeSection>
              )}

              {Array.isArray(extended.countOnMeFor) && extended.countOnMeFor.length > 0 && (
                <ResumeSection label="COUNT ON ME FOR">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {extended.countOnMeFor.map((s, i) => <span key={i} className="portal-chip">{s}</span>)}
                  </div>
                </ResumeSection>
              )}

              {Array.isArray(gathering) && gathering.length > 0 && (
                <ResumeSection label="DOWN FOR">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {gathering.map((s, i) => <span key={i} className="portal-chip">{s}</span>)}
                  </div>
                </ResumeSection>
              )}
            </div>
          </div>

          {/* Right: Admin actions */}
          <div>
            {app?.invitedByDisplayName && (
              <ResumeSection label="VOUCHED BY">
                <p className="portal-serif" style={{ fontSize: 16, color: '#C9A24B', fontStyle: 'italic' }}>
                  {app.invitedByHandle ? (
                    <Link to={`/${app.invitedByHandle}`} style={{ color: '#C9A24B', textDecoration: 'none' }}>{app.invitedByDisplayName} <span className="portal-mono" style={{ fontSize: 11, color: 'rgba(236,231,218,0.34)', marginLeft: 4 }}>@{app.invitedByHandle}</span></Link>
                  ) : app.invitedByDisplayName}
                </p>
              </ResumeSection>
            )}
            <ResumeSection label="WHO TOLD THEM">
              <p className="portal-serif" style={{ fontSize: 16, color: '#ECE7DA', whiteSpace: 'pre-wrap' }}>{app?.referrerText || '—'}</p>
            </ResumeSection>

            <ResumeSection label="WHAT THEY'D BRING">
              <p className="portal-serif" style={{ fontSize: 16, color: '#ECE7DA', whiteSpace: 'pre-wrap' }}>{app?.pitchText || '—'}</p>
            </ResumeSection>

            <hr className="portal-hr" />

            {(app?.status === 'pending' || !app?.status) ? (
              <>
                <p className="portal-label" style={{ marginBottom: 8 }}>PRIVATE NOTE (OPTIONAL)</p>
                <textarea
                  className="portal-input"
                  placeholder="Just for the admin record."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={2000}
                  style={{ marginBottom: 16 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button onClick={() => review('accept')} disabled={busy} className="portal-btn solid">{busy ? 'WORKING…' : 'ACCEPT'}</button>
                  <button onClick={() => review('not_yet')} disabled={busy} className="portal-btn ghost">NOT YET</button>
                  <button onClick={() => review('decline')} disabled={busy} className="portal-btn danger">DECLINE</button>
                </div>
              </>
            ) : (
              <div className="portal-card" style={{ padding: 16 }}>
                <p className="portal-label" style={{ marginBottom: 8 }}>REVIEWED</p>
                <p className="portal-serif" style={{ fontSize: 16 }}>
                  {app.status.toUpperCase()}
                  {app.reviewedByDisplayName ? ` by ${app.reviewedByDisplayName}` : ''}
                  {app.reviewedAt ? ` on ${new Date(app.reviewedAt).toLocaleDateString()}` : ''}
                </p>
                {app.decisionNote && (
                  <p className="portal-serif" style={{ fontSize: 14, color: 'rgba(236,231,218,0.56)', marginTop: 8, fontStyle: 'italic' }}>"{app.decisionNote}"</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </PortalShell>
  );
}

function ResumeSection({ label, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <p className="portal-label" style={{ marginBottom: 8 }}>{label}</p>
      {children}
    </div>
  );
}

function parseJSON(s) { if (!s) return null; try { return JSON.parse(s); } catch { return null; } }
